import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { NO_SCORE_REASON_MAX_LENGTH } from "score-common";

// Card #1930 — the reason prompt for the "No score recorded" disposition
// (score-api's markRideNoScoreRecorded). This is the honest third exit for a
// ridden-but-blank ride: it replaces inventing marks (WEC 2026-08-21). The
// reason is REQUIRED, so Save stays disabled until one is typed, and it is
// kept in the ride's audit trail. The server re-checks (400 reason_required)
// and refuses a ride that already has marks (409 ride_has_marks); both come
// back through `error` and are shown with the server's own curated message.
//
// Not modal-gating anything about scoring: this is an admin screen action, a
// scribe can still score the ride at any point (#1930 AC8).
export default function NoScoreRecordedDialog({ open, rideLabel, saving, error, onClose, onConfirm }) {
  const { t } = useTranslation("scoreadmin");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const trimmed = reason.trim();
  const serverMessage = error?.response?.data?.data?.message;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" data-testid="no-score-dialog">
      <DialogTitle>{t("noScoreDialog.title")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {rideLabel ? `${rideLabel}. ` : ""}
          {t("noScoreDialog.body")}
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label={t("noScoreDialog.reasonLabel")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: NO_SCORE_REASON_MAX_LENGTH, "data-testid": "no-score-reason" }}
          helperText={t("noScoreDialog.reasonHelp")}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="no-score-error">
            {serverMessage || t("noScoreDialog.error")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} data-testid="no-score-cancel">
          {t("noScoreDialog.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(trimmed)}
          disabled={saving || trimmed.length === 0}
          data-testid="no-score-confirm"
        >
          {saving ? t("noScoreDialog.saving") : t("noScoreDialog.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
